import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "sonner";

// Context
import { SidebarProvider } from "./contexts/SidebarContext";

// Components
import GlobalCommandPalette from "./components/GlobalCommandPalette";

// Pages
import LandingPage from "./pages/LandingPage";
import PortalLandingPage from "./pages/PortalLandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import InviteActivationPage from "./pages/InviteActivationPage";
import OnboardingPage from "./pages/OnboardingPage";
import Dashboard from "./pages/Dashboard";
import CommandCenter from "./pages/CommandCenter";
import DealsPage from "./pages/DealsPage";
import DealDetailPage from "./pages/DealDetailPage";
import PipelinePage from "./pages/PipelinePage";
import BuyersPage from "./pages/BuyersPage";
import SettingsPage from "./pages/SettingsPage";
import PaymentHistoryPage from "./pages/PaymentHistoryPage";
import UpgradePage from "./pages/UpgradePage";
import AuthCallback from "./pages/AuthCallback";
import MarketplacePage from "./pages/MarketplacePage";
import ContractsPage from "./pages/ContractsPage";

// New Feature Pages
import DealPacketsPage from "./pages/DealPacketsPage";
import ListImportPage from "./pages/ListImportPage";
import ImportedListsPage from "./pages/ImportedListsPage";
import SMSBlastPage from "./pages/SMSBlastPage";
import StrategySimulationPage from "./pages/StrategySimulationPage";
import AdminPage from "./pages/AdminPage";
import AdminUsersPage from "./pages/AdminUsers";
import AdminDealsPage from "./pages/AdminDeals";
import AdminPaymentsPage from "./pages/AdminPayments";
import AdminReferralsPage from "./pages/AdminReferralsPage";
import AdminPortalAccess from "./pages/AdminPortalAccess";
import TeamPage from "./pages/TeamPage";
import SignContractPage from "./pages/SignContractPage";
import PublicDocSignPage from "./pages/PublicDocSignPage";
import PublicDealPackagePage from "./pages/PublicDealPackagePage";
import SupportPage from "./pages/SupportPage";
import PerformanceDashboardPage from "./pages/PerformanceDashboardPage";
import UnderwritingListPage from "./pages/UnderwritingListPage";
import MultifamilyUnderwritingPage from "./pages/MultifamilyUnderwritingPage";
import CapitalModelPage from "./pages/CapitalModelPage";
import DealMarketplacePage from "./pages/DealMarketplacePage";

// Messaging & Network Pages
import MessagesPage from "./pages/MessagesPage";
import NetworkHubPage from "./pages/NetworkHubPage";
import ExchangePage from "./pages/ExchangePage";
import RealtorsPage from "./pages/RealtorsPage";
import RealtorProfilePage from "./pages/RealtorProfilePage";
import TitleCompaniesPage from "./pages/TitleCompaniesPage";
import TitleCompanyProfilePage from "./pages/TitleCompanyProfilePage";

// Mockup
import DashboardMockup from "./pages/DashboardMockup";
import CapitalCommandMockup from "./pages/CapitalCommandMockup";

// Contractor Pages
import ContractorRegisterPage from "./pages/ContractorRegisterPage";
import ContractorLoginPage from "./pages/ContractorLoginPage";
import ContractorDashboardPage from "./pages/ContractorDashboardPage";
import ContractorCommandCenter from "./pages/ContractorCommandCenter";

// Lender Pages
import LenderPortal from "./pages/LenderPortal";
import LenderCommandCenter from "./pages/LenderCommandCenter";
import LenderAuth from "./pages/LenderAuth";
import LendersSearchPage from "./pages/LendersSearchPage";

// Realtor Pages
import RealtorAuthPage from "./pages/realtor/RealtorAuthPage";
import RealtorDashboardPage from "./pages/realtor/RealtorDashboardPage";
import RealtorCommandCenter from "./pages/realtor/RealtorCommandCenter";
import RealtorListingsPage from "./pages/realtor/RealtorListingsPage";
import RealtorTransactionsPage from "./pages/realtor/RealtorTransactionsPage";
import RealtorBuyersPage from "./pages/realtor/RealtorBuyersPage";
import RealtorSellersPage from "./pages/realtor/RealtorSellersPage";
import RealtorOffersComparePage from "./pages/realtor/RealtorOffersComparePage";
import RealtorTitleRequestsPage from "./pages/realtor/RealtorTitleRequestsPage";
import RealtorOpportunitiesPage from "./pages/realtor/RealtorOpportunitiesPage";

// Title Portal Pages
import TitleAuthPage from "./pages/title/TitleAuthPage";
import TitleDashboardPage from "./pages/title/TitleDashboardPage";
import TitleCommandCenter from "./pages/title/TitleCommandCenter";
import TitleOrdersPage from "./pages/title/TitleOrdersPage";
import TitleOrderDetailPage from "./pages/title/TitleOrderDetailPage";
import TitleMarketplacePage from "./pages/title/TitleMarketplacePage";
import TitleOpportunitiesPage from "./pages/title/TitleOpportunitiesPage";
import TitleEscrowDashboard from "./pages/title/TitleEscrowDashboard";

// Waitlist Pages
import WaitlistPage from "./pages/WaitlistPage";
import WaitlistAdminPage from "./pages/WaitlistAdminPage";

// Early Access Pages
// EarlyAccessPage removed - consolidated into WaitlistPage
import LaunchConfirmedPage from "./pages/LaunchConfirmedPage";
import ApplicationReceivedPage from "./pages/ApplicationReceivedPage";
import DocumentIntelligencePage from "./pages/DocumentIntelligencePage";
import NetworkCreditsPage from "./pages/NetworkCreditsPage";
import DialerPage from "./pages/DialerPage";
import CertVerifyPage from "./pages/CertVerifyPage";
import ReputationPage from "./pages/ReputationPage";
import WhatIsDealForgePage from "./pages/WhatIsDealForgePage";
import InviteLandingPage from "./pages/InviteLandingPage";
import CalendarPage from "./pages/CalendarPage";
import ActivateSubscriptionPage from "./pages/ActivateSubscriptionPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
export const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [contractorLoading, setContractorLoading] = useState(false); // Start as false, only set true when needed

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      
      // Get subscription status separately — don't fail auth if this errors
      try {
        const subResponse = await axios.get(`${BACKEND_URL}/api/subscription/status`, {
          withCredentials: true
        });
        setSubscription(subResponse.data);
      } catch {
        setSubscription(null);
      }
    } catch (error) {
      setUser(null);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const checkContractorAuth = async () => {
    setContractorLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contractor/me`, {
        withCredentials: true
      });
      setContractor(response.data);
    } catch (error) {
      setContractor(null);
    } finally {
      setContractorLoading(false);
    }
  };

  useEffect(() => {
    // Run auth checks in parallel but don't block on contractor check
    checkAuth();
    // Only check contractor auth if we might be on a contractor page
    if (window.location.pathname.startsWith('/contractor')) {
      checkContractorAuth();
    }
  }, []);

  const login = (userData) => {
    setUser(userData);
    setLoading(false);
    // Fetch subscription status after login
    axios.get(`${BACKEND_URL}/api/subscription/status`, { withCredentials: true })
      .then(res => setSubscription(res.data))
      .catch(() => setSubscription(null));
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setSubscription(null);
  };

  const contractorLogin = (contractorData) => {
    setContractor(contractorData);
  };

  const contractorLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/contractor/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Contractor logout error:", error);
    }
    setContractor(null);
  };

  return children({ 
    user, loading, login, logout, subscription, checkAuth,
    contractor, contractorLoading, contractorLogin, contractorLogout, checkContractorAuth
  });
};

// Protected Route
const ProtectedRoute = ({ children, user, loading, subscription }) => {
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin shadow-[0_0_15px_rgba(212,175,55,0.3)]" />
          <p className="text-white/50 font-medium">Loading DealForge...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Require active subscription (or admin bypass)
  const subStatus = subscription?.subscription_status;
  const hasAccess = user?.is_admin || subStatus === "active" || subStatus === "trialing";
  if (!hasAccess) {
    return <Navigate to="/activate-subscription" replace />;
  }
  
  return children;
};

// Contractor Protected Route
const ContractorProtectedRoute = ({ children, contractor, loading, user }) => {
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin shadow-[0_0_15px_rgba(212,175,55,0.3)]" />
          <p className="text-white/50 font-medium">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Allow admin impersonation - if user is admin and viewing portal, allow access
  if (user?.is_admin) {
    return children;
  }
  
  if (!contractor) {
    return <Navigate to="/contractor/login" state={{ from: location }} replace />;
  }
  
  return children;
};

// App Router with session_id detection
function AppRouter({ user, loading, login, logout, subscription, checkAuth, contractor, contractorLoading, contractorLogin, contractorLogout }) {
  const location = useLocation();
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [deals, setDeals] = useState([]);
  
  // Global keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (user) { // Only show if logged in
          setShowCommandPalette(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user]);

  // Fetch deals for command palette
  useEffect(() => {
    if (user) {
      axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true })
        .then(res => setDeals(res.data || []))
        .catch(() => setDeals([]));
    }
  }, [user]);
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={login} />;
  }

  return (
    <>
      {/* Global Command Palette */}
      <GlobalCommandPalette 
        isOpen={showCommandPalette} 
        onClose={() => setShowCommandPalette(false)}
        deals={deals}
      />
      
      <Routes>
      {/* Waitlist at root for pre-launch */}
      <Route path="/" element={<WaitlistPage />} />
      <Route path="/join" element={<WaitlistPage />} />
      <Route path="/waitlist" element={<WaitlistPage />} />
      <Route path="/early-access" element={<WaitlistPage />} />
      
      {/* SEO Pages */}
      <Route path="/what-is-dealforge" element={<WhatIsDealForgePage />} />
      
      {/* Deal Invite Landing */}
      <Route path="/invite/:deal_id" element={<InviteLandingPage />} />
      
      {/* Early Access System */}
      <Route path="/earlyaccess" element={<WaitlistPage />} />
      <Route path="/launch-confirmed" element={<LaunchConfirmedPage />} />
      <Route path="/application-received" element={<ApplicationReceivedPage />} />
      
      {/* UI Mockup - Infrastructure Style */}
      <Route path="/mockup" element={<DashboardMockup />} />
      <Route path="/command" element={<CapitalCommandMockup />} />
      
      {/* Full marketing landing page (ready for launch) */}
      <Route path="/launch" element={<LandingPage user={user} />} />
      <Route path="/launch/pricing" element={<LandingPage user={user} scrollToPricing />} />
      
      {/* Portal Landing Pages - only show for non-logged-in users */}
      <Route path="/investors" element={user ? <Navigate to="/dashboard" replace /> : <PortalLandingPage />} />
      <Route path="/contractors" element={user ? <Navigate to="/contractor/dashboard" replace /> : <PortalLandingPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={login} />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage onLogin={login} />} />
      <Route path="/activate" element={user ? <Navigate to="/dashboard" replace /> : <InviteActivationPage onLogin={login} />} />
      <Route path="/onboarding" element={user ? <OnboardingPage user={user} /> : <Navigate to="/login" replace />} />
      
      {/* Contractor Public Routes */}
      <Route path="/contractor-portal" element={<Navigate to="/contractor/login" replace />} />
      <Route path="/contractor" element={
        contractor ? <Navigate to="/contractor/dashboard" replace /> : <Navigate to="/contractor/login" replace />
      } />
      <Route path="/contractor/register" element={
        contractor ? <Navigate to="/contractor/dashboard" replace /> : <ContractorRegisterPage onContractorLogin={contractorLogin} />
      } />
      <Route path="/contractor/login" element={
        contractor ? <Navigate to="/contractor/dashboard" replace /> : <ContractorLoginPage onContractorLogin={contractorLogin} />
      } />
      
      {/* Contractor Protected Routes */}
      <Route path="/contractor/dashboard" element={
        <ContractorProtectedRoute contractor={contractor} loading={contractorLoading || loading} user={user}>
          <ContractorCommandCenter contractor={contractor} onLogout={contractorLogout} user={user} />
        </ContractorProtectedRoute>
      } />
      
      {/* Lender Public Routes */}
      <Route path="/lender/login" element={<LenderAuth />} />
      <Route path="/lender/register" element={<LenderAuth />} />
      
      {/* Lender Portal (Protected by lender auth internally) */}
      <Route path="/lender/portal" element={<LenderCommandCenter user={user} loading={loading} />} />
      <Route path="/lender" element={<LenderPortal />} />
      <Route path="/lender/profile" element={<LenderPortal />} />
      <Route path="/lender/subscription/success" element={<LenderPortal />} />
      <Route path="/lender/subscription/cancel" element={<LenderPortal />} />
      
      {/* Realtor Portal */}
      <Route path="/realtor" element={<RealtorAuthPage />} />
      <Route path="/realtor/login" element={<RealtorAuthPage />} />
      <Route path="/realtor/register" element={<RealtorAuthPage />} />
      <Route path="/realtor/dashboard" element={<RealtorCommandCenter user={user} loading={loading} />} />
      <Route path="/realtor/opportunities" element={<RealtorOpportunitiesPage />} />
      <Route path="/realtor/listings" element={<RealtorListingsPage />} />
      <Route path="/realtor/listings/new" element={<RealtorListingsPage />} />
      <Route path="/realtor/listings/:listingId" element={<RealtorListingsPage />} />
      <Route path="/realtor/listings/:listingId/compare" element={<RealtorOffersComparePage />} />
      <Route path="/realtor/transactions" element={<RealtorTransactionsPage />} />
      <Route path="/realtor/transactions/:transactionId" element={<RealtorTransactionsPage />} />
      <Route path="/realtor/buyers" element={<RealtorBuyersPage />} />
      <Route path="/realtor/sellers" element={<RealtorSellersPage />} />
      <Route path="/realtor/title-services" element={<RealtorTitleRequestsPage />} />
      <Route path="/realtor/commission" element={<RealtorDashboardPage />} />
      <Route path="/realtor/analytics" element={<RealtorDashboardPage />} />
      <Route path="/realtor/settings" element={<RealtorDashboardPage />} />
      
      {/* Title Portal */}
      <Route path="/title" element={<TitleAuthPage />} />
      <Route path="/title/login" element={<TitleAuthPage />} />
      <Route path="/title/register" element={<TitleAuthPage />} />
      <Route path="/title/dashboard" element={<TitleCommandCenter user={user} loading={loading} />} />
      <Route path="/title/opportunities" element={<TitleOpportunitiesPage />} />
      <Route path="/title/orders" element={<TitleOrdersPage />} />
      <Route path="/title/orders/new" element={<TitleOrdersPage />} />
      <Route path="/title/orders/:orderId" element={<TitleOrderDetailPage />} />
      <Route path="/title/marketplace" element={<Navigate to="/network?tab=deals" replace />} />
      <Route path="/title/deal-wallet" element={<TitleEscrowDashboard />} />
      <Route path="/title/deal-wallet/:dealId" element={<TitleEscrowDashboard />} />
      <Route path="/title/searches" element={<TitleDashboardPage />} />
      <Route path="/title/closings" element={<TitleDashboardPage />} />
      <Route path="/title/requests" element={<TitleDashboardPage />} />
      <Route path="/title/revenue" element={<TitleDashboardPage />} />
      <Route path="/title/analytics" element={<TitleDashboardPage />} />
      <Route path="/title/settings" element={<TitleDashboardPage />} />
      
      {/* Messages & Network (Protected) */}
      <Route path="/messages" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/network" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <NetworkHubPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/network/exchange" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <ExchangePage user={user} />
        </ProtectedRoute>
      } />
      
      {/* Activate Subscription — for logged-in users without active sub */}
      <Route path="/activate-subscription" element={
        loading ? (
          <div className="min-h-screen bg-black flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : user ? (
          <ActivateSubscriptionPage user={user} onLogout={logout} />
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <CommandCenter user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/deals" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <DealsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/deals/:dealId" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <DealDetailPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/execution-flow" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <PipelinePage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/pipeline" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <PipelinePage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/marketplace" element={<Navigate to="/network?tab=partners" replace />} />
      <Route path="/lenders" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <LendersSearchPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/contracts" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <ContractsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/buyers" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <BuyersPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/deal-packets" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <DealPacketsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/import" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <ListImportPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/imported-lists" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <ImportedListsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/sms" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <SMSBlastPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/dialer" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <DialerPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/compare" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <StrategySimulationPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/title-services" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <RealtorTitleRequestsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/realtors" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <RealtorsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/realtors/:realtorId" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <RealtorProfilePage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/title-companies" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <TitleCompaniesPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/title-companies/:companyId" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <TitleCompanyProfilePage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/performance" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <PerformanceDashboardPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/underwriting" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <UnderwritingListPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/underwriting/new" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <MultifamilyUnderwritingPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/underwriting/:analysisId" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <MultifamilyUnderwritingPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/calculator" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <CapitalModelPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/deal-marketplace" element={<Navigate to="/network?tab=deals" replace />} />
      <Route path="/admin" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <AdminPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <AdminUsersPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/admin/deals" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <AdminDealsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <AdminPaymentsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/admin/waitlist" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <WaitlistAdminPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/admin/portals" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <AdminPortalAccess user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/admin/referrals" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <AdminReferralsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <TeamPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/document-intelligence" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <DocumentIntelligencePage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/network-credits" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <NetworkCreditsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <SettingsPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/payments" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <PaymentHistoryPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <SupportPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/upgrade" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <UpgradePage user={user} subscription={subscription} onLogout={logout} onUpgrade={checkAuth} />
        </ProtectedRoute>
      } />
      <Route path="/reputation" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <ReputationPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute user={user} loading={loading} subscription={subscription}>
          <CalendarPage user={user} subscription={subscription} onLogout={logout} />
        </ProtectedRoute>
      } />
      
      {/* Public Routes — no auth required */}
      <Route path="/sign/doc/:token" element={<PublicDocSignPage />} />
      <Route path="/deal-package/:token" element={<PublicDealPackagePage />} />
      <Route path="/sign/:token" element={<SignContractPage />} />
      <Route path="/verify/:certId" element={<CertVerifyPage />} />
      <Route path="/verify" element={<CertVerifyPage />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    
    {/* Mobile Navigation - only show when user is logged in */}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <div className="dark noise-overlay">
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            background: '#121212',
            border: '1px solid #27272A',
            color: '#FAFAFA'
          }
        }}
      />
      <SidebarProvider>
        <BrowserRouter>
          <AuthContext>
            {(authProps) => <AppRouter {...authProps} />}
          </AuthContext>
        </BrowserRouter>
      </SidebarProvider>
    </div>
    </HelmetProvider>
  );
}

export default App;
